import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Type definitions for optimized export structure
export interface ExportData {
  context: {
    provider_name: string;
    version_number: string;
    region_name: string;
    export_timestamp: string;
  };
  data: {
    channels: any[];
    plans: any[];
    categories: any[];
    channel_types: any[];
    packages: any[];
    channel_tags: any[];
    relationships: {
      channel_plans: any[];
      channel_numbers: any[];
      channel_categories: any[];
      package_plans: any[];
      package_categories: any[];
      package_channels: any[];
      channel_tag_assignments: any[];
    };
  };
  summary: {
    total_channels: number;
    total_plans: number;
    total_categories: number;
    total_channel_types: number;
    total_packages: number;
    total_channel_tags: number;
  };
}

// Type definitions for optimized channel-centric export structure
export interface OptimizedExportData {
  context: {
    provider_name: string;
    version_number: string;
    region_name: string;
    export_timestamp: string;
    provider_type: {
      has_sequence_numbers: boolean;
      numbering_system: 'sequential' | 'explicit';
    };
  };
  optimizations: {
    single_plan_mode?: {
      enabled: boolean;
      plan_info: { id: number; name: string };
      notice: string;
    };
    single_category_mode?: {
      enabled: boolean;
      category_info: { id: number; name: string };
      notice: string;
    };
    field_optimizations: {
      description?: { removed: boolean; reason: string; affected_channels: number };
      price?: { removed: boolean; reason: string; affected_channels: number };
      is_local?: { removed: boolean; default_value: boolean; reason: string; affected_channels: number };
      plan_ids?: { removed: boolean; reason: string };
      channel_numbers?: { removed: boolean; reason: string; affected_channels: number };
    };
  };
  data: {
    channels: any[]; // Conditionally optimized channels
    plans: any[];
    categories: any[];
    channel_types: any[];
    packages: any[];
    channel_tags: any[];
  };
  summary: {
    total_channels: number;
    total_plans: number;
    total_categories: number;
    total_channel_types: number;
    total_packages: number;
    total_channel_tags: number;
    space_optimization: {
      original_structure_items: number;
      optimized_structure_items: number;
      fields_removed: number;
      reduction_percentage: string;
    };
  };
}

// Type definitions for static features export structure
export interface StaticFeaturesExportData {
  context: {
    provider_name: string;
    version_number: string;
    region_name: string;
    export_timestamp: string;
  };
  provider_info: {
    type_info: any;
    business_info: any;
  };
  features: {
    streaming_specifications: any[];
    additional_fees: any[];
    promotional_offers: any[];
    content_coverage: any[];
    service_terms: any[];
    service_quality: any[];
    provider_device_support: any[];
  };
  summary: {
    total_features: number;
  };
}

export class ExportService {
  private static async getProvider(providerId: number) {
    const { data, error } = await supabase
      .from('providers')
      .select('*')
      .eq('id', providerId)
      .single();

    if (error) throw new Error(`Failed to fetch provider: ${error.message}`);
    return data;
  }

  private static async getVersion(versionId: number) {
    const { data, error } = await supabase
      .from('provider_versions')
      .select('*')
      .eq('id', versionId)
      .single();

    if (error) throw new Error(`Failed to fetch version: ${error.message}`);
    return data;
  }

  private static async getRegion(regionId: number) {
    const { data, error } = await supabase
      .from('regions')
      .select('*')
      .eq('id', regionId)
      .single();

    if (error) throw new Error(`Failed to fetch region: ${error.message}`);
    return data;
  }

  private static async getChannels(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('channels')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch channels: ${error.message}`);
    return data || [];
  }

  private static async getPlans(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch plans: ${error.message}`);
    return data || [];
  }

  private static async getCategories(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch categories: ${error.message}`);
    return data || [];
  }

  private static async getChannelTypes(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('channel_types')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch channel types: ${error.message}`);
    return data || [];
  }

  private static async getPackages(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('packages')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch packages: ${error.message}`);
    return data || [];
  }

  private static async getChannelTags(providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('channel_tags')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch channel tags: ${error.message}`);
    return data || [];
  }


  private static async getChannelPlans(_providerId: number, versionId: number) {
    const { data, error } = await supabase
      .from('channel_plans')
      .select('*')
      .eq('version_id', versionId);

    if (error) throw new Error(`Failed to fetch channel plans: ${error.message}`);
    return data || [];
  }

  private static async getChannelNumbers(channelIds: number[], regionId: number) {
    if (channelIds.length === 0) return [];
    
    const { data, error } = await supabase
      .from('channel_numbers')
      .select('*')
      .in('channel_id', channelIds)
      .or(`location_id.eq.${regionId},location_id.is.null`); // Include both region-specific AND null location_ids

    if (error) throw new Error(`Failed to fetch channel numbers: ${error.message}`);
    return data || [];
  }

  private static async getChannelCategories(channelIds: number[]) {
    if (channelIds.length === 0) return [];

    const { data, error } = await supabase
      .from('channel_categories')
      .select('*')
      .in('channel_id', channelIds);

    if (error) throw new Error(`Failed to fetch channel categories: ${error.message}`);
    return data || [];
  }

  private static async getPackagePlans(packageIds: number[]) {
    if (packageIds.length === 0) return [];

    const { data, error } = await supabase
      .from('package_plans')
      .select('*')
      .in('package_id', packageIds);

    if (error) throw new Error(`Failed to fetch package plans: ${error.message}`);
    return data || [];
  }

  private static async getPackageCategories(packageIds: number[]) {
    if (packageIds.length === 0) return [];

    const { data, error } = await supabase
      .from('package_categories')
      .select('*')
      .in('package_id', packageIds);

    if (error) throw new Error(`Failed to fetch package categories: ${error.message}`);
    return data || [];
  }

  private static async getPackageChannels(packageIds: number[]) {
    if (packageIds.length === 0) return [];

    const { data, error } = await supabase
      .from('package_channels')
      .select('*')
      .in('package_id', packageIds);

    if (error) throw new Error(`Failed to fetch package channels: ${error.message}`);
    return data || [];
  }

  private static async getChannelTagAssignments(channelIds: number[]) {
    if (channelIds.length === 0) return [];

    const { data, error } = await supabase
      .from('channel_tag_assignments')
      .select('*')
      .in('channel_id', channelIds);

    if (error) throw new Error(`Failed to fetch channel tag assignments: ${error.message}`);
    return data || [];
  }

  // Static features methods
  private static async getStreamingSpecifications(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('streaming_specifications')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch streaming specifications: ${error.message}`);
    return data || [];
  }

  private static async getAdditionalFees(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('additional_fees')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch additional fees: ${error.message}`);
    return data || [];
  }

  private static async getPromotionalOffers(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('promotional_offers')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch promotional offers: ${error.message}`);
    return data || [];
  }

  private static async getContentCoverage(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('content_coverage')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch content coverage: ${error.message}`);
    return data || [];
  }

  private static async getServiceTerms(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('service_terms')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch service terms: ${error.message}`);
    return data || [];
  }

  private static async getServiceQuality(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('service_quality')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch service quality: ${error.message}`);
    return data || [];
  }

  private static async getProviderDeviceSupport(providerId: number, versionId: number, regionId: number) {
    const { data, error } = await supabase
      .from('provider_device_support')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .eq('region_id', regionId);

    if (error) throw new Error(`Failed to fetch provider device support: ${error.message}`);
    return data || [];
  }

  private static async getProviderType(typeId: number | null) {
    if (!typeId) return null;
    
    const { data, error } = await supabase
      .from('provider_types')
      .select('*')
      .eq('id', typeId)
      .single();

    if (error) throw new Error(`Failed to fetch provider type: ${error.message}`);
    return data;
  }

  // Clean data by removing unnecessary fields for comparison
  private static cleanDataObject(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;
    
    const cleaned = { ...obj };
    
    // Remove timestamps (not needed for comparison)
    delete cleaned.created_at;
    delete cleaned.updated_at;
    delete cleaned.published_at;
    delete cleaned.effective_date;
    
    // Remove system IDs (context is provided at top level)
    delete cleaned.id;
    delete cleaned.provider_id;
    delete cleaned.version_id;
    delete cleaned.region_id;
    delete cleaned.location_id; // Same as region_id in channel_numbers
    
    return cleaned;
  }

  // Clean arrays of data
  private static cleanDataArray(arr: any[]): any[] {
    return arr.map(item => this.cleanDataObject(item));
  }

  static async exportProviderData(providerId: number, versionId: number): Promise<ExportData> {
    try {
      // Get basic info for context
      const provider = await this.getProvider(providerId);
      const version = await this.getVersion(versionId);
      const region = await this.getRegion(version.region_id);

      // Get core data (channels, plans, categories)
      const rawChannels = await this.getChannels(providerId, versionId);
      const rawPlans = await this.getPlans(providerId, versionId);
      const rawCategories = await this.getCategories(providerId, versionId);
      const rawChannelTypes = await this.getChannelTypes(providerId, versionId);
      const rawPackages = await this.getPackages(providerId, versionId);
      const rawChannelTags = await this.getChannelTags(providerId, versionId);

      // Get relationships
      const rawChannelPlans = await this.getChannelPlans(providerId, versionId);
      const channelIds = rawChannels.map(c => c.id);
      const packageIds = rawPackages.map(p => p.id);
      const rawChannelNumbers = await this.getChannelNumbers(channelIds, version.region_id);
      const rawChannelCategories = await this.getChannelCategories(channelIds);
      const rawPackagePlans = await this.getPackagePlans(packageIds);
      const rawPackageCategories = await this.getPackageCategories(packageIds);
      const rawPackageChannels = await this.getPackageChannels(packageIds);
      const rawChannelTagAssignments = await this.getChannelTagAssignments(channelIds);

      // Clean all data (remove timestamps, IDs, etc.)
      const channels = this.cleanDataArray(rawChannels);
      const plans = this.cleanDataArray(rawPlans);
      const categories = this.cleanDataArray(rawCategories);
      const channelTypes = this.cleanDataArray(rawChannelTypes);
      const packages = this.cleanDataArray(rawPackages);
      const channelTags = this.cleanDataArray(rawChannelTags);
      const channelPlans = this.cleanDataArray(rawChannelPlans);
      const channelNumbers = this.cleanDataArray(rawChannelNumbers);
      const channelCategories = this.cleanDataArray(rawChannelCategories);
      const packagePlans = this.cleanDataArray(rawPackagePlans);
      const packageCategories = this.cleanDataArray(rawPackageCategories);
      const packageChannels = this.cleanDataArray(rawPackageChannels);
      const channelTagAssignments = this.cleanDataArray(rawChannelTagAssignments);

      const exportData: ExportData = {
        context: {
          provider_name: provider.name,
          version_number: version.version_number,
          region_name: region.name,
          export_timestamp: new Date().toISOString(),
        },
        data: {
          channels,
          plans,
          categories,
          channel_types: channelTypes,
          packages,
          channel_tags: channelTags,
          relationships: {
            channel_plans: channelPlans,
            channel_numbers: channelNumbers,
            channel_categories: channelCategories,
            package_plans: packagePlans,
            package_categories: packageCategories,
            package_channels: packageChannels,
            channel_tag_assignments: channelTagAssignments,
          },
        },
        summary: {
          total_channels: channels.length,
          total_plans: plans.length,
          total_categories: categories.length,
          total_channel_types: channelTypes.length,
          total_packages: packages.length,
          total_channel_tags: channelTags.length,
        },
      };

      return exportData;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unknown error occurred during export");
      throw error;
    }
  }

  static async exportStaticFeaturesData(providerId: number, versionId: number): Promise<StaticFeaturesExportData> {
    try {
      // Get basic info for context
      const provider = await this.getProvider(providerId);
      const version = await this.getVersion(versionId);
      const region = await this.getRegion(version.region_id);

      // Get provider type info
      const providerType = await this.getProviderType(provider.type_id);

      // Get 7-feature system data
      const rawStreamingSpecs = await this.getStreamingSpecifications(providerId, versionId, version.region_id);
      const rawAdditionalFees = await this.getAdditionalFees(providerId, versionId, version.region_id);
      const rawPromotionalOffers = await this.getPromotionalOffers(providerId, versionId, version.region_id);
      const rawContentCoverage = await this.getContentCoverage(providerId, versionId, version.region_id);
      const rawServiceTerms = await this.getServiceTerms(providerId, versionId, version.region_id);
      const rawServiceQuality = await this.getServiceQuality(providerId, versionId, version.region_id);
      const rawDeviceSupport = await this.getProviderDeviceSupport(providerId, versionId, version.region_id);

      // Clean provider info (keep only essential business info)
      const cleanProviderType = this.cleanDataObject(providerType);
      const cleanProviderBusiness = {
        has_sequence_numbers: provider.has_sequence_numbers,
        currency: provider.currency,
        support_email: provider.support_email,
        website_url: provider.website_url,
        support_phone: provider.support_phone,
        free_trial_days: provider.free_trial_days,
        student_discount_available: provider.student_discount_available,
      };

      // Clean all feature data
      const streamingSpecs = this.cleanDataArray(rawStreamingSpecs);
      const additionalFees = this.cleanDataArray(rawAdditionalFees);
      const promotionalOffers = this.cleanDataArray(rawPromotionalOffers);
      const contentCoverage = this.cleanDataArray(rawContentCoverage);
      const serviceTerms = this.cleanDataArray(rawServiceTerms);
      const serviceQuality = this.cleanDataArray(rawServiceQuality);
      const deviceSupport = this.cleanDataArray(rawDeviceSupport);

      const totalFeatures = streamingSpecs.length + additionalFees.length + promotionalOffers.length + 
                          contentCoverage.length + serviceTerms.length + serviceQuality.length + deviceSupport.length;

      const exportData: StaticFeaturesExportData = {
        context: {
          provider_name: provider.name,
          version_number: version.version_number,
          region_name: region.name,
          export_timestamp: new Date().toISOString(),
        },
        provider_info: {
          type_info: cleanProviderType,
          business_info: cleanProviderBusiness,
        },
        features: {
          streaming_specifications: streamingSpecs,
          additional_fees: additionalFees,
          promotional_offers: promotionalOffers,
          content_coverage: contentCoverage,
          service_terms: serviceTerms,
          service_quality: serviceQuality,
          provider_device_support: deviceSupport,
        },
        summary: {
          total_features: totalFeatures,
        },
      };

      return exportData;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unknown error occurred during static features export");
      throw error;
    }
  }

  static downloadJSON(data: ExportData | StaticFeaturesExportData | OptimizedExportData, filename: string) {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  static generateFilename(provider: string, version: string, timestamp: string, type: 'channels' | 'static' | 'optimized' = 'channels'): string {
    const cleanProvider = provider.replace(/[^a-zA-Z0-9]/g, '_');
    const cleanVersion = version.replace(/[^a-zA-Z0-9]/g, '_');
    const dateStr = new Date(timestamp).toISOString().split('T')[0];
    let typePrefix = '';
    if (type === 'static') typePrefix = 'static_';
    else if (type === 'optimized') typePrefix = 'optimized_';
    return `${typePrefix}${cleanProvider}_${cleanVersion}_${dateStr}.json`;
  }

  // Enhanced optimized export method with conditional field optimization
  static async exportOptimizedProviderData(providerId: number, versionId: number): Promise<OptimizedExportData> {
    try {
      // Get basic info for context
      const provider = await this.getProvider(providerId);
      const version = await this.getVersion(versionId);
      const region = await this.getRegion(version.region_id);

      // Get core data
      const rawChannels = await this.getChannels(providerId, versionId);
      const rawPlans = await this.getPlans(providerId, versionId);
      const rawCategories = await this.getCategories(providerId, versionId);
      const rawChannelTypes = await this.getChannelTypes(providerId, versionId);
      const rawPackages = await this.getPackages(providerId, versionId);
      const rawChannelTags = await this.getChannelTags(providerId, versionId);

      // Get relationships
      const rawChannelPlans = await this.getChannelPlans(providerId, versionId);
      const channelIds = rawChannels.map(c => c.id);
      const rawChannelNumbers = await this.getChannelNumbers(channelIds, version.region_id);

      // Analyze data for conditional optimizations
      const optimizationAnalysis = this.analyzeOptimizations(rawChannels, rawPlans, rawCategories, rawChannelNumbers, provider);

      // Create optimized channels with conditional field removal
      const optimizedChannels = rawChannels.map(channel => {
        const cleanChannel = this.cleanDataObject(channel);
        
        // Find all plans this channel belongs to
        const channelPlanRelations = rawChannelPlans.filter(cp => cp.channel_id === channel.id);
        const planIds = channelPlanRelations.map(cp => cp.plan_id);
        
        // Find channel numbers for this channel with channel type info
        const channelNumbers = rawChannelNumbers
          .filter(cn => cn.channel_id === channel.id)
          .map(cn => {
            const cleanCN = this.cleanDataObject(cn);
            // Add channel type name from rawChannelTypes
            const channelType = rawChannelTypes.find(ct => ct.id === cn.type_id);
            return {
              ...cleanCN,
              type: channelType ? channelType.name : 'Unknown'
            };
          });

        // Build optimized channel object conditionally
        const optimizedChannel: any = {
          name: cleanChannel.name
        };

        // Only add fields if they're not being optimized away
        if (!optimizationAnalysis.single_category_mode) {
          optimizedChannel.category_id = cleanChannel.category_id;
        }

        if (!optimizationAnalysis.field_optimizations.description.remove) {
          optimizedChannel.description = cleanChannel.description;
        } else if (cleanChannel.description && cleanChannel.description.trim() !== '') {
          // Only include description if this specific channel has one
          optimizedChannel.description = cleanChannel.description;
        }

        if (!optimizationAnalysis.field_optimizations.price.remove) {
          optimizedChannel.price = cleanChannel.price;
        } else if (cleanChannel.price !== null) {
          // Only include price if this specific channel has one
          optimizedChannel.price = cleanChannel.price;
        }

        if (!optimizationAnalysis.field_optimizations.is_local.remove) {
          optimizedChannel.is_local = cleanChannel.is_local;
        }

        if (!optimizationAnalysis.single_plan_mode) {
          optimizedChannel.plan_ids = planIds;
        }

        if (!optimizationAnalysis.field_optimizations.channel_numbers.remove) {
          optimizedChannel.channel_numbers = channelNumbers;
        }

        return optimizedChannel;
      });

      // Clean other data arrays normally
      const plans = this.cleanDataArray(rawPlans);
      const categories = this.cleanDataArray(rawCategories);
      const channelTypes = this.cleanDataArray(rawChannelTypes);
      const packages = this.cleanDataArray(rawPackages);
      const channelTags = this.cleanDataArray(rawChannelTags);

      // Calculate enhanced space optimization metrics
      const originalItems = rawChannels.length + rawPlans.length + rawCategories.length +
                           rawChannelTypes.length + rawChannelPlans.length + rawChannelNumbers.length +
                           rawPackages.length + rawChannelTags.length;
      const fieldsRemoved = this.calculateFieldsRemoved(rawChannels.length, optimizationAnalysis);
      const optimizedItems = optimizedChannels.length + plans.length + categories.length + channelTypes.length +
                            packages.length + channelTags.length;
      const reductionPercentage = ((originalItems + fieldsRemoved - optimizedItems) / (originalItems + fieldsRemoved) * 100).toFixed(1);

      // Build optimizations object for the response
      const optimizations = this.buildOptimizationsResponse(optimizationAnalysis, rawPlans, rawCategories, rawChannels.length);

      const exportData: OptimizedExportData = {
        context: {
          provider_name: provider.name,
          version_number: version.version_number,
          region_name: region.name,
          export_timestamp: new Date().toISOString(),
          provider_type: {
            has_sequence_numbers: provider.has_sequence_numbers,
            numbering_system: provider.has_sequence_numbers ? 'sequential' : 'explicit'
          }
        },
        optimizations,
        data: {
          channels: optimizedChannels,
          plans,
          categories,
          channel_types: channelTypes,
          packages,
          channel_tags: channelTags,
        },
        summary: {
          total_channels: optimizedChannels.length,
          total_plans: plans.length,
          total_categories: categories.length,
          total_channel_types: channelTypes.length,
          total_packages: packages.length,
          total_channel_tags: channelTags.length,
          space_optimization: {
            original_structure_items: originalItems,
            optimized_structure_items: optimizedItems,
            fields_removed: fieldsRemoved,
            reduction_percentage: `${reductionPercentage}%`,
          },
        },
      };

      return exportData;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unknown error occurred during enhanced optimized export");
      throw error;
    }
  }

  // Helper method to analyze what optimizations are possible
  private static analyzeOptimizations(rawChannels: any[], rawPlans: any[], _rawCategories: any[], rawChannelNumbers: any[], provider: any) {
    // Analyze plans
    const uniquePlans = [...new Set(rawPlans.map(p => p.id))];
    const single_plan_mode = uniquePlans.length === 1;

    // Analyze categories  
    const uniqueCategories = [...new Set(rawChannels.map(c => c.category_id).filter(Boolean))];
    const single_category_mode = uniqueCategories.length === 1;

    // Analyze field usage
    const channelsWithDescription = rawChannels.filter(c => c.description && c.description.trim() !== '').length;
    const channelsWithPrice = rawChannels.filter(c => c.price !== null).length;
    const localChannels = rawChannels.filter(c => c.is_local === true).length;
    const channelsWithNumbers = rawChannelNumbers.length > 0;
    const isSequentialProvider = provider.has_sequence_numbers === true;

    return {
      single_plan_mode,
      single_category_mode,
      field_optimizations: {
        description: {
          remove: channelsWithDescription === 0,
          channels_with_data: channelsWithDescription,
          total_channels: rawChannels.length
        },
        price: {
          remove: channelsWithPrice === 0,
          channels_with_data: channelsWithPrice,
          total_channels: rawChannels.length
        },
        is_local: {
          remove: localChannels === 0 || localChannels === rawChannels.length,
          all_local: localChannels === rawChannels.length,
          all_non_local: localChannels === 0,
          channels_with_data: localChannels,
          total_channels: rawChannels.length
        },
        channel_numbers: {
          remove: !channelsWithNumbers || isSequentialProvider,
          channels_with_data: rawChannelNumbers.length,
          total_channels: rawChannels.length,
          is_sequential: isSequentialProvider
        }
      }
    };
  }

  // Helper method to build the optimizations response object
  private static buildOptimizationsResponse(analysis: any, rawPlans: any[], rawCategories: any[], totalChannels: number) {
    const optimizations: any = {
      field_optimizations: {}
    };

    // Single plan mode
    if (analysis.single_plan_mode && rawPlans.length > 0) {
      optimizations.single_plan_mode = {
        enabled: true,
        plan_info: { id: rawPlans[0].id, name: rawPlans[0].name },
        notice: `All ${totalChannels} channels belong to the single plan '${rawPlans[0].name}' (ID: ${rawPlans[0].id})`
      };
    }

    // Single category mode  
    if (analysis.single_category_mode && rawCategories.length > 0) {
      const category = rawCategories[0];
      optimizations.single_category_mode = {
        enabled: true,
        category_info: { id: category.id, name: category.name },
        notice: `All ${totalChannels} channels belong to the single category '${category.name}' (ID: ${category.id})`
      };
    }

    // Field optimizations
    if (analysis.field_optimizations.description.remove) {
      optimizations.field_optimizations.description = {
        removed: true,
        reason: `All ${totalChannels} channels had empty descriptions`,
        affected_channels: totalChannels
      };
    }

    if (analysis.field_optimizations.price.remove) {
      optimizations.field_optimizations.price = {
        removed: true,
        reason: `All ${totalChannels} channels had null prices`,
        affected_channels: totalChannels
      };
    }

    if (analysis.field_optimizations.is_local.remove) {
      const defaultValue = analysis.field_optimizations.is_local.all_local;
      optimizations.field_optimizations.is_local = {
        removed: true,
        default_value: defaultValue,
        reason: `All ${totalChannels} channels are ${defaultValue ? 'local' : 'non-local'}`,
        affected_channels: totalChannels
      };
    }

    if (analysis.single_plan_mode) {
      optimizations.field_optimizations.plan_ids = {
        removed: true,
        reason: 'Single plan mode enabled - all channels use the same plan'
      };
    }

    if (analysis.field_optimizations.channel_numbers.remove) {
      const reason = analysis.field_optimizations.channel_numbers.is_sequential 
        ? 'Sequential provider - channels numbered by display order (1, 2, 3, etc.)'
        : 'No channels have channel numbers assigned';
      optimizations.field_optimizations.channel_numbers = {
        removed: true,
        reason,
        affected_channels: totalChannels
      };
    }

    return optimizations;
  }

  // Helper method to calculate how many fields were removed
  private static calculateFieldsRemoved(totalChannels: number, analysis: any): number {
    let fieldsRemoved = 0;

    if (analysis.single_plan_mode) fieldsRemoved += totalChannels; // plan_ids arrays
    if (analysis.single_category_mode) fieldsRemoved += totalChannels; // category_id fields
    if (analysis.field_optimizations.description.remove) fieldsRemoved += totalChannels;
    if (analysis.field_optimizations.price.remove) fieldsRemoved += totalChannels;
    if (analysis.field_optimizations.is_local.remove) fieldsRemoved += totalChannels;
    if (analysis.field_optimizations.channel_numbers.remove) fieldsRemoved += totalChannels;

    return fieldsRemoved;
  }
}